import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Send, Loader2, Mail, MousePointerClick, Eye, AlertTriangle, Save, FlaskConical, Zap, BarChart3, FileText, Megaphone, CheckCircle2, Plus, Trash2, RotateCcw } from 'lucide-react';
import api, { formatApiError } from '@/lib/adminApi';

const inputCls = 'w-full rounded-xl border border-white/10 bg-elevated px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-brand/50 focus:outline-none';
const btnPrimary = 'flex items-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-ink hover:opacity-90 disabled:opacity-40';
const btnGhost = 'flex items-center gap-2 rounded-xl border border-white/10 bg-elevated px-4 py-2 text-sm text-white/80 hover:bg-white/5 disabled:opacity-40';

const StatCard = ({ icon: Icon, label, value, accent = '#17FDFD' }) => (
  <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-wider text-white/50">{label}</span>
      <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}1a`, color: accent }}><Icon className="h-4 w-4" /></span>
    </div>
    <div className="mt-3 font-display text-3xl font-bold text-white">{value}</div>
  </div>
);

const KIND_BADGE = { transactional: 'bg-white/10 text-white/70', automation: 'bg-brand/15 text-brand', campaign: 'bg-[#FF2E93]/15 text-[#FF2E93]', test: 'bg-yellow-400/15 text-yellow-400' };

const StatsTab = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/admin/marketing/stats').then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Send} label="Emails Sent" value={stats.total_sent} />
        <StatCard icon={Eye} label="Opens" value={stats.events.open ?? '—'} accent="#00FFA3" />
        <StatCard icon={MousePointerClick} label="Clicks" value={stats.events.click ?? '—'} accent="#FFD700" />
        <StatCard icon={AlertTriangle} label="Bounces / Failed" value={(stats.events.bounce || 0) + stats.failed} accent="#FF2E93" />
      </div>
      {!stats.webhook_configured && (
        <div data-testid="marketing-webhook-hint" className="flex items-start gap-3 rounded-2xl border border-yellow-400/20 bg-yellow-400/5 p-5">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-400" />
          <p className="text-sm leading-relaxed text-white/60">
            <span className="text-white">Opens &amp; clicks need one manual step:</span> in SendGrid go to <span className="text-white">Settings → Mail Settings → Event Webhook</span>, set the HTTP POST URL to
            <span className="mx-1 rounded bg-white/10 px-1.5 py-0.5 font-mono text-xs text-brand">{stats.webhook_url}</span>
            and tick Delivered, Opened, Clicked, Bounced, Spam Reports. Stats appear here automatically after that.
          </p>
        </div>
      )}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface/70">
        <p className="border-b border-white/10 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white/40">Recent sends</p>
        {stats.recent.length === 0 ? <p className="px-5 py-8 text-sm text-white/40">No emails sent yet.</p> : (
          <table className="w-full text-left text-sm">
            <tbody>
              {stats.recent.map((r) => (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="px-5 py-2.5 text-white/80">{r.to}</td>
                  <td className="px-5 py-2.5 text-white/50">{r.subject}</td>
                  <td className="px-5 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${KIND_BADGE[r.kind] || 'bg-white/10 text-white/60'}`}>{r.kind}</span></td>
                  <td className="px-5 py-2.5">{r.status === 'sent' ? <CheckCircle2 className="h-4 w-4 text-success" /> : <AlertTriangle className="h-4 w-4 text-destructive" />}</td>
                  <td className="px-5 py-2.5 text-xs text-white/40">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const TemplateEditor = ({ tpl, onSaved, onDeleted }) => {
  const [form, setForm] = useState({ subject: tpl.subject, html: tpl.html, enabled: tpl.enabled });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/marketing/templates/${tpl.key}`, form);
      toast.success('Template saved');
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testTo) return toast.error('Enter a test email address');
    try {
      await api.post(`/admin/marketing/templates/${tpl.key}/test`, { to: testTo });
      toast.success(`Test sent to ${testTo}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reset = async () => {
    if (!window.confirm('Reset this template to its original default content? Your edits will be lost.')) return;
    try {
      await api.post(`/admin/marketing/templates/${tpl.key}/reset`);
      toast.success('Template reset to default');
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete template "${tpl.name}" permanently?\n\nIt will also be removed from the automation funnel and any queued sends using it will be cancelled.`)) return;
    try {
      const { data } = await api.delete(`/admin/marketing/templates/${tpl.key}`);
      toast.success(`Template deleted${data.queued_cancelled ? ` (${data.queued_cancelled} queued emails cancelled)` : ''}`);
      onDeleted();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-brand/20 bg-surface/70 p-5">
      <p className="text-xs text-white/40">{tpl.description} · <span className="font-mono text-white/30">key: {tpl.key}</span></p>
      <input data-testid="template-subject" className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" />
      <textarea data-testid="template-html" rows={10} className={`${inputCls} font-mono text-xs leading-relaxed`} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} />
      <div className="rounded-xl border border-white/10 bg-black p-4" dangerouslySetInnerHTML={{ __html: form.html }} />
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={busy} data-testid="template-save" className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
        <label className="flex items-center gap-2 text-sm text-white/70">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-[#17FDFD]" /> Enabled
        </label>
        <button onClick={reset} data-testid="template-reset" className={btnGhost}><RotateCcw className="h-4 w-4" /> Reset to default</button>
        <button onClick={remove} data-testid="template-delete" className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2 text-sm text-white/50 hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /> Delete</button>
        <div className="ml-auto flex items-center gap-2">
          <input className={`${inputCls} w-56`} placeholder="you@copy.click" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          <button onClick={sendTest} data-testid="template-test" className={btnGhost}><FlaskConical className="h-4 w-4" /> Send test</button>
        </div>
      </div>
    </div>
  );
};

const NewTemplateForm = ({ onCreated, onCancel }) => {
  const [form, setForm] = useState({ name: '', subject: '', category: 'custom', html: '<h2 style="margin:0 0 16px;color:#ffffff;font-size:18px">Your headline</h2>\n<p style="color:#b8c4ca;font-size:14px;line-height:1.7">Write your email here. You can use {{email}} as a variable.</p>' });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!form.name || !form.subject) return toast.error('Name and subject are required');
    setBusy(true);
    try {
      await api.post('/admin/marketing/templates', form);
      toast.success('Template created');
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-brand/20 bg-surface/70 p-5">
      <p className="font-display text-sm font-semibold text-white">New email template</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <input data-testid="new-template-name" className={inputCls} placeholder="Template name (e.g. Provider outreach)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
          <option value="custom" className="bg-surface">custom</option>
          <option value="automation" className="bg-surface">automation</option>
          <option value="transactional" className="bg-surface">transactional</option>
        </select>
      </div>
      <input data-testid="new-template-subject" className={inputCls} placeholder="Subject line" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
      <textarea data-testid="new-template-html" rows={8} className={`${inputCls} font-mono text-xs leading-relaxed`} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} />
      <div className="rounded-xl border border-white/10 bg-black p-4" dangerouslySetInnerHTML={{ __html: form.html }} />
      <div className="flex gap-2">
        <button onClick={create} disabled={busy} data-testid="new-template-save" className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Create template</button>
        <button onClick={onCancel} className="rounded-xl border border-white/10 px-4 py-2 text-sm text-white/60 hover:bg-white/5">Cancel</button>
      </div>
    </div>
  );
};

const TemplatesTab = () => {
  const [templates, setTemplates] = useState(null);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const fetchAll = useCallback(() => api.get('/admin/marketing/templates').then((r) => setTemplates(r.data.templates)), []);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  if (!templates) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  return (
    <div className="space-y-3">
      {!creating ? (
        <button onClick={() => setCreating(true)} data-testid="new-template-btn" className={btnPrimary}><Plus className="h-4 w-4" /> New template</button>
      ) : (
        <NewTemplateForm onCreated={() => { setCreating(false); fetchAll(); }} onCancel={() => setCreating(false)} />
      )}
      {templates.map((t) => (
        <div key={t.key}>
          <button data-testid="template-row" onClick={() => setOpen(open === t.key ? null : t.key)} className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-surface/70 px-5 py-4 text-left hover:border-brand/30">
            <div>
              <p className="font-display text-sm font-semibold text-white">{t.name}</p>
              <p className="text-xs text-white/40">Subject: {t.subject}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${KIND_BADGE[t.category] || 'bg-white/10 text-white/60'}`}>{t.category}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${t.enabled ? 'bg-success/15 text-success' : 'bg-white/10 text-white/40'}`}>{t.enabled ? 'On' : 'Off'}</span>
            </div>
          </button>
          {open === t.key && <div className="mt-2"><TemplateEditor tpl={t} onSaved={fetchAll} onDeleted={() => { setOpen(null); fetchAll(); }} /></div>}
        </div>
      ))}
    </div>
  );
};

const CampaignsTab = () => {
  const [campaigns, setCampaigns] = useState(null);
  const [form, setForm] = useState({ subject: '', html: '<h2 style="margin:0 0 16px;color:#ffffff;font-size:18px">Hello traders 👋</h2>\n<p style="color:#b8c4ca;font-size:14px;line-height:1.7">Write your announcement here...</p>' });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');
  const fetchAll = useCallback(() => api.get('/admin/marketing/campaigns').then((r) => setCampaigns(r.data.campaigns)), []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendTest = async () => {
    if (!testTo) return toast.error('Enter a test email address');
    try {
      await api.post(`/admin/marketing/campaigns/test?to=${encodeURIComponent(testTo)}`, form);
      toast.success(`Test sent to ${testTo}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendAll = async () => {
    if (!form.subject) return toast.error('Add a subject first');
    if (!window.confirm('Send this campaign to ALL newsletter subscribers now?')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/admin/marketing/campaigns', form);
      toast.success(`Sending to ${data.campaign.recipients_count} subscribers`);
      setForm({ ...form, subject: '' });
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3 rounded-2xl border border-white/10 bg-surface/70 p-5">
        <p className="font-display text-sm font-semibold text-white">New campaign</p>
        <input data-testid="campaign-subject" className={inputCls} placeholder="Subject line" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        <textarea data-testid="campaign-html" rows={8} className={`${inputCls} font-mono text-xs leading-relaxed`} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} />
        <div className="rounded-xl border border-white/10 bg-black p-4" dangerouslySetInnerHTML={{ __html: form.html }} />
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={sendAll} disabled={busy} data-testid="campaign-send" className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />} Send to all subscribers</button>
          <div className="ml-auto flex items-center gap-2">
            <input className={`${inputCls} w-56`} placeholder="you@copy.click" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
            <button onClick={sendTest} data-testid="campaign-test" className={btnGhost}><FlaskConical className="h-4 w-4" /> Send test</button>
          </div>
        </div>
      </div>
      {campaigns?.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface/70">
          <p className="border-b border-white/10 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white/40">Past campaigns</p>
          <table className="w-full text-left text-sm">
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} data-testid="campaign-row" className="border-b border-white/5">
                  <td className="px-5 py-3 text-white">{c.subject}</td>
                  <td className="px-5 py-3 text-white/50">{c.sent_count}/{c.recipients_count} sent</td>
                  <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.status === 'sent' ? 'bg-success/15 text-success' : 'bg-yellow-400/15 text-yellow-400'}`}>{c.status}</span></td>
                  <td className="px-5 py-3 text-xs text-white/40">{new Date(c.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AutomationTab = () => {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState('');
  const fetchAll = useCallback(() => {
    api.get('/admin/marketing/automation').then((r) => setData(r.data));
    api.get('/admin/marketing/templates').then((r) => setTemplates(r.data.templates));
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  if (!data) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const updateStep = (i, patch) => setData({ ...data, steps: data.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const removeStep = (i) => setData({ ...data, steps: data.steps.filter((_, idx) => idx !== i) });
  const addStep = () => {
    if (!newKey) return toast.error('Pick a template for the new step');
    if (data.steps.some((s) => s.template_key === newKey)) return toast.error('That template is already a step');
    const maxDelay = Math.max(0, ...data.steps.map((s) => s.delay_days));
    setData({ ...data, steps: [...data.steps, { template_key: newKey, delay_days: maxDelay + 2, enabled: true }] });
    setNewKey('');
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put('/admin/marketing/automation', { steps: data.steps });
      toast.success('Funnel saved — applies to every new subscriber');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const available = templates.filter((t) => !data.steps.some((s) => s.template_key === t.key));

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-2xl border border-brand/20 bg-brand/5 p-5">
        <Zap className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
        <p className="text-sm leading-relaxed text-white/60">Every new newsletter subscriber automatically enters this funnel. Edit the email content in the Templates tab; control timing here. <span className="text-white">{data.queue.pending}</span> emails queued, <span className="text-white">{data.queue.sent}</span> already sent by automation.</p>
      </div>
      <div className="space-y-3">
        {data.steps.length === 0 && <p className="text-sm text-white/40">No steps — the funnel is off. Add a step below.</p>}
        {data.steps.map((s, i) => (
          <div key={s.template_key} data-testid="automation-step" className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-surface/70 px-5 py-4">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/15 font-display text-sm font-bold text-brand">{i + 1}</span>
            <div className="min-w-[180px] flex-1">
              <p className="text-sm font-medium text-white">{templates.find((t) => t.key === s.template_key)?.name || s.template_key}</p>
              <p className="text-xs text-white/40">{s.delay_days === 0 ? 'Sends instantly on signup' : `Sends ${s.delay_days} day${s.delay_days > 1 ? 's' : ''} after signup`}</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-white/50">Delay (days)
              <input type="number" min={0} max={60} value={s.delay_days} onChange={(e) => updateStep(i, { delay_days: Math.max(0, parseInt(e.target.value || 0, 10)) })} className="w-16 rounded-lg border border-white/10 bg-elevated px-2 py-1.5 text-center text-sm text-white" />
            </label>
            <label className="flex items-center gap-2 text-xs text-white/50">
              <input type="checkbox" checked={s.enabled} onChange={(e) => updateStep(i, { enabled: e.target.checked })} className="accent-[#17FDFD]" /> Active
            </label>
            <button onClick={() => removeStep(i)} data-testid="automation-step-remove" title="Remove step" className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="automation-add-select" value={newKey} onChange={(e) => setNewKey(e.target.value)} className="rounded-xl border border-white/10 bg-elevated px-4 py-2.5 text-sm text-white focus:border-brand/50 focus:outline-none">
          <option value="" className="bg-surface">Add a step — pick template…</option>
          {available.map((t) => <option key={t.key} value={t.key} className="bg-surface">{t.name}</option>)}
        </select>
        <button onClick={addStep} data-testid="automation-add-step" className={btnGhost}><Plus className="h-4 w-4" /> Add step</button>
        <button onClick={save} disabled={busy} data-testid="automation-save" className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save funnel</button>
      </div>
    </div>
  );
};

const TABS = [
  { key: 'stats', label: 'Stats', icon: BarChart3, el: StatsTab },
  { key: 'templates', label: 'Templates', icon: FileText, el: TemplatesTab },
  { key: 'campaigns', label: 'Campaigns', icon: Megaphone, el: CampaignsTab },
  { key: 'automation', label: 'Automation', icon: Zap, el: AutomationTab },
];

export default function AdminMarketing() {
  const [tab, setTab] = useState('stats');
  const Active = TABS.find((t) => t.key === tab).el;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Email Marketing</h1>
        <p className="mt-1 text-sm text-white/50">Templates, automated funnels, campaigns and delivery stats — all sending live via SendGrid from noreply@copy.click.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`marketing-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? 'bg-brand/10 text-brand' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      <Active />
    </div>
  );
}
